import React, { useState, useMemo } from 'react';
import { useAPI } from '../Hooks/useAPIs';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Refresh, Search, Clear } from '@mui/icons-material';

const extractDTCEntries = (data) => {
    if (Array.isArray(data)) return data;
    if (data.dtc_entries || data.entries) return data.dtc_entries || data.entries;
    if (data.data && Array.isArray(data.data)) return data.data;

    const possibleArrayKeys = ['dtcs', 'codes', 'diagnostics', 'results'];
    for (const key of possibleArrayKeys) {
      if (data[key] && Array.isArray(data[key])) return data[key];
    }

    if (typeof data === 'object' && data !== null) {
      return Object.keys(data).map(key => ({ module: key, ...data[key] }));
    }

    return [data];
};

const processEntry = (entry, index) => {
    const base = {
      id: `dtc_${index}`,
      module: 'Unknown',
      response: 'Unknown',
      ecu_description: 'N/A',
      ecu_code: 'N/A',
      dtc_description: 'N/A',
      dtc_code: 'N/A',
      status_mask: 'N/A',
      failure_type_byte: 'N/A',
    };

    if (typeof entry === 'object' && entry !== null) {
      return {
        ...base,
        module: entry.module || entry.ecu || entry.component || base.module,
        response: entry.response || entry.response_type || entry.status || base.response,
        ecu_description: entry.ecu_description || entry.ecu_desc || entry.description || base.ecu_description,
        ecu_code: entry.ecu_code || entry.ecu_id || entry.code || base.ecu_code,
        dtc_description: entry.dtc_description || entry.dtc_desc || entry.fault_description || base.dtc_description,
        dtc_code: entry.dtc_code || entry.dtc || entry.fault_code || base.dtc_code,
        status_mask: entry.status_mask || entry.status || entry.mask || base.status_mask,
        failure_type_byte: entry.failure_type_byte || entry.failure_type || entry.type || base.failure_type_byte,
      };
    }

    if (typeof entry === 'string') {
      return { ...base, dtc_description: entry };
    }

    return {
      ...base,
      id: 'raw_data',
      module: 'Raw Data',
      response: typeof entry === 'string' ? 'String' : 'Object',
      dtc_description: typeof entry === 'string' ? entry : JSON.stringify(entry),
    };
};

const convertDTCDataToTable = (data) => {
    if (!data) return [];
    const dtcEntries = extractDTCEntries(data);
    return dtcEntries.map((entry, index) => processEntry(entry, index));
};

const createColumnFilter = (placeholder) => ({ column }) => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={column.getFilterValue() ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
          endAdornment: column.getFilterValue() && (
            <InputAdornment position="end">
              <Clear fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => column.setFilterValue('')} />
            </InputAdornment>
          ),
        },
      }}
      sx={{ width: '100%' }}
    />
);

const cellStyles = {
    module: { fontFamily: 'monospace', fontWeight: 'bold' },
    ecu_description: { fontSize: '12px', wordBreak: 'break-word' },
    ecu_code: { fontFamily: 'monospace', fontSize: '12px' },
    dtc_description: { fontSize: '12px', wordBreak: 'break-word', whiteSpace: 'normal' },
    monospace: { fontFamily: 'monospace', fontSize: '12px' },
};

const columnConfigs = [
    { key: 'module', header: 'Module', size: 120, placeholder: 'Search module...', cellType: 'styled', style: cellStyles.module },
    { key: 'response', header: 'Response', size: 100, placeholder: 'Search response...', cellType: 'chip' },
    { key: 'dtc_description', header: 'DTC Description', size: 250, placeholder: 'Search DTC description...', cellType: 'styled', style: cellStyles.dtc_description },
    { key: 'ecu_code', header: 'ECU Code', size: 100, placeholder: 'Search ECU code...', cellType: 'styled', style: cellStyles.ecu_code },
    { key: 'ecu_description', header: 'ECU Description', size: 200, placeholder: 'Search ECU description...', cellType: 'styled', style: cellStyles.ecu_description },
    { key: 'status_mask', header: 'Status Mask', size: 100, placeholder: 'Search status...', cellType: 'styled', style: cellStyles.monospace },
    { key: 'failure_type_byte', header: 'Failure Type', size: 120, placeholder: 'Search failure type...', cellType: 'styled', style: cellStyles.monospace },
];

const createCellComponent = (config) => ({ cell }) => {
    const value = cell.getValue();
    if (config.cellType === 'chip') {
      return <Typography variant="body2" sx={config.style}>{value}</Typography>;
    }
    return <Typography variant="body2" sx={config.style}>{value}</Typography>;
};

const DTCFileComponent = ({ fileLocation, filename }) => {
  const [viewMode, setViewMode] = useState('table');
  const [columnFilters, setColumnFilters] = useState([]);
  const { apiCall } = useAPI();

  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dtcFile', fileLocation],
    queryFn: async () => {
      if (!fileLocation) throw new Error('No file location provided');
      const response = await apiCall('get', '/api/v1/dtc_logs/by_filename', {
        params: { file_location: fileLocation },
      });
      return response.data.data;
    },
    enabled: !!fileLocation,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    retry: 1,
  });

  const tableData = useMemo(() => {
    if (!rawData) return [];
    return convertDTCDataToTable(rawData);
  }, [rawData]);

  const columns = useMemo(() =>
    columnConfigs.map(config => ({
      accessorKey: config.key,
      header: config.header,
      size: config.size,
      enableColumnFilter: true,
      filterFn: 'includesString',
      Cell: createCellComponent(config),
      Filter: createColumnFilter(config.placeholder),
    })), []
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    onColumnFiltersChange: setColumnFilters,
    columnFilterDisplayMode: 'subheader',
    muiTableContainerProps: { sx: { maxHeight: 700 } },
    initialState: { density: 'compact', showColumnFilters: true },
    state: { columnFilters },
    enablePagination: false,
    muiTableBodyRowProps: ({ index }) => ({
      sx: {
        backgroundColor: index % 2 === 0 ? 'inherit' : 'action.hover',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
      },
    }),
    muiTableProps: {
      sx: {
        '& .MuiTableCell-root': { padding: '8px', borderBottom: '1px solid', borderBottomColor: 'divider' },
        '& .MuiTableHead-root .MuiTableCell-root': { backgroundColor: 'var(--mui-palette-background-default)', fontWeight: 'bold' },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          DTC Entries ({tableData.length} total)
        </Typography>
      </Box>
    ),
    enableColumnActions: true,
    enableSorting: true,
    enableTopToolbar: true,
    enableBottomToolbar: false,
  });

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading DTC file...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {error?.message || 'Failed to load DTC file'}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => refetch()} 
          startIcon={<Refresh />}
          sx={{ 
            textTransform: 'none',
            borderColor: 'divider'
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!rawData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          No DTC data available
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => refetch()} 
          startIcon={<Refresh />}
          sx={{ 
            textTransform: 'none',
            borderColor: 'divider'
          }}
        >
          Load DTC File
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {viewMode === 'table' ? (
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'divider', 
          overflow: 'hidden',
          backgroundColor: 'background.paper'
        }}>
          <MaterialReactTable table={table} />
        </Box>
      ) : (
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'divider', 
          p: 3, 
          maxHeight: 600, 
          overflow: 'auto'
        }}>
          <pre style={{ 
            whiteSpace: 'pre-wrap', 
            wordBreak: 'break-word', 
            fontFamily: 'monospace', 
            fontSize: '13px', 
            lineHeight: 1.6, 
            margin: 0
          }}>
            {typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2)}
          </pre>
        </Box>
      )}
    </Box>
  );
};

export default DTCFileComponent;